import { NavigationMenuProps, NavigationRouteProps } from '@msc-b2c/interfaces/types';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Text } from '@msc-b2c/shared-components';
import { breakpoints, getActiveSession } from '@msc-b2c/shared-utils';
import { useEffect, useState } from 'react';
import './navigationMenu.module.scss';
import { ROUTE_CHANGED } from '../utils/myAreaUtils';
import { BookingStatusEnum, LoginMode } from '@msc-b2c/interfaces/enums';
import { getDeviceType } from '../utils/utils';

export function NavigationMenu({
  booking,
  hasBooking = true,
  navigationItems,
  allMyCruisesLink,
  noLinkedBookingLink,
  cartLink,
  checkoutLink,
  menuExpandAriaLabel,
  menuCollapseAriaLabel,
}: Readonly<NavigationMenuProps>) {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeFirstLevel, setActiveFirstLevel] = useState<NavigationRouteProps>();
  const [activeSecondLevel, setActiveSecondLevel] = useState<NavigationRouteProps>();
  const [isNavigationMenuHidden, setIsNavigationMenuHidden] = useState<boolean>(false);
  const [menuOpened, setMenuOpened] = useState<boolean>(false);
  const [isMobileNav, setIsMobileNav] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < breakpoints.lg
  );
  const deviceType = getDeviceType();
  const isPanelHidden = isMobileNav && !menuOpened;

  const initOpenIndex = (): number => {
    const firstLevelIndex = navigationItems?.findIndex((item) =>
      window.location.pathname.includes(item?.urlLink?.url ?? '')
    );
    return firstLevelIndex && firstLevelIndex !== -1 ? firstLevelIndex : 0;
  };

  const [openIndex, setOpenIndex] = useState<number | null>(initOpenIndex());
  const loginMode = getActiveSession().loginMode;
  const bookingStatus = booking?.status?.code;

  /**
   * Set active first and second level items based on the current URL.
   */
  useEffect(() => {
    if (navigationItems) {
      const firstLevelItem = navigationItems?.find((item) =>
        window.location.pathname.includes(item?.urlLink?.url ?? '')
      );
      const secondLevelItem = firstLevelItem?.children?.find((child) =>
        window.location.pathname?.replace('.html', '')?.includes(child?.urlLink?.url ?? '')
      );
      const allMyCruisesItem = window.location.pathname
        ?.replace('.html', '')
        ?.includes(allMyCruisesLink?.url ?? '');

      const noLinkedBookingItem = window.location.pathname
        ?.replace('.html', '')
        ?.includes(noLinkedBookingLink?.url ?? '');

      const cartItem = window.location.pathname
        ?.replace('.html', '')
        ?.includes(cartLink?.url ?? '');

      const checkoutItem = window.location.pathname
        ?.replace('.html', '')
        ?.includes(checkoutLink?.url ?? '');

      if (navigationItems?.length) {
        setActiveFirstLevel(firstLevelItem);

        const shouldFallbackToFirstChild =
          !allMyCruisesItem && !noLinkedBookingItem && !cartItem && !checkoutItem;
        let activeSecondLevelItem: NavigationRouteProps | undefined;
        if (hasBooking) {
          activeSecondLevelItem = secondLevelItem;
          if (!activeSecondLevelItem && shouldFallbackToFirstChild) {
            activeSecondLevelItem = firstLevelItem?.children?.[0];
          }
        }

        setActiveSecondLevel(activeSecondLevelItem);
        toggleAccordion(navigationItems?.indexOf(firstLevelItem as NavigationRouteProps));

        if (
          secondLevelItem ||
          allMyCruisesItem ||
          noLinkedBookingItem ||
          cartItem ||
          checkoutItem ||
          firstLevelItem?.children?.[0]
        ) {
          let currentRoute =
            secondLevelItem?.urlLink?.url ??
            (allMyCruisesItem ? allMyCruisesLink?.url : undefined) ??
            (noLinkedBookingItem ? noLinkedBookingLink?.url : undefined) ??
            (cartItem ? cartLink?.url : undefined) ??
            (checkoutItem ? checkoutLink?.url : undefined) ??
            firstLevelItem?.children?.[0]?.urlLink?.url;

          currentRoute = currentRoute?.replace(
            firstLevelItem?.urlLink?.url?.replace('.html', '') ?? '',
            ''
          );

          currentRoute && navigate(`${currentRoute}${location.search}`);
        }
      }
    }
  }, [navigationItems, hasBooking]);

  useEffect(() => {
    const mobileNavQuery = globalThis.matchMedia(`(max-width: ${breakpoints.lg - 1}px)`);
    const syncMobileNav = () => setIsMobileNav(mobileNavQuery.matches);

    syncMobileNav();
    mobileNavQuery.addEventListener('change', syncMobileNav);

    return () => mobileNavQuery.removeEventListener('change', syncMobileNav);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('has-open-navigation-menu', menuOpened);

    return () => {
      document.body.classList.remove('has-open-navigation-menu');
    };
  }, [menuOpened]);

  useEffect(() => {
    setMenuOpened(false);
  }, [location.pathname]);

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const hasVisibleSecondLevelItems = (item?: NavigationRouteProps): boolean => {
    return Boolean(hasBooking && item?.children?.length);
  };

  const getFirstLevelItemIcon = (item: NavigationRouteProps, index: number): string => {
    if (!hasVisibleSecondLevelItems(item)) {
      return '';
    }

    return openIndex === index ? 'up' : 'down';
  };

  const handleClick = (event: MouseEvent | KeyboardEvent | undefined, index: number) => {
    const hasVisibleChildren = hasVisibleSecondLevelItems(navigationItems?.[index]);

    if (window.innerWidth < breakpoints.xl) {
      if (!hasVisibleChildren) {
        window.location.href = navigationItems?.[index]?.urlLink?.url ?? '';
        return;
      }

      event?.preventDefault();
      toggleAccordion(index);
    } else {
      window.location.href = navigationItems?.[index]?.urlLink?.url ?? '';
    }
  };

  useEffect(() => {
    if (!hasBooking) {
      setActiveSecondLevel(undefined);
      document.querySelector('body')?.classList.remove('has-submenu');
      return;
    }

    if (activeFirstLevel) {
      !displaySecondItemsLevel(activeFirstLevel) && setActiveSecondLevel(undefined);
    }

    const secondLevelItem = activeFirstLevel?.children?.find((child) =>
      window.location.pathname?.replace('.html', '')?.includes(child?.urlLink?.url ?? '')
    );

    if (secondLevelItem) {
      setActiveSecondLevel(secondLevelItem);
    }

    document.querySelector('body')?.classList.toggle('has-submenu', !!secondLevelItem);

    // Hide navigation menu for cart and checkout pages
    const cartOrCheckoutItem =
      window.location.pathname?.replace('.html', '')?.includes(cartLink?.url ?? '') ||
      window.location.pathname?.replace('.html', '')?.includes(checkoutLink?.url ?? '');

    setIsNavigationMenuHidden(cartOrCheckoutItem);
  }, [location, hasBooking]);

  const updateSelectedHeader = (
    firstLevel: NavigationRouteProps,
    secondLevel: NavigationRouteProps,
    secondLevelRoute: string
  ) => {
    setActiveSecondLevel(secondLevel);
    setMenuOpened(false);

    window.dispatchEvent(new CustomEvent(ROUTE_CHANGED));

    if (activeFirstLevel === firstLevel) {
      navigate(secondLevelRoute);
    } else {
      setActiveFirstLevel(firstLevel);
      window.location.href = firstLevel.urlLink.url + secondLevelRoute;
    }
  };

  const displaySecondItemsLevel = (firstLevelItem: NavigationRouteProps): boolean => {
    const isToBeRendered = firstLevelItem?.children?.find((childItem) => {
      return window.location.pathname
        .replace('.html/', '/')
        ?.includes(childItem?.urlLink?.url ?? '');
    });

    return !!isToBeRendered;
  };

  return (
    <div
      className={`cmp-navigation-menu__container ${
        hasBooking && activeSecondLevel?.label ? '' : '-no-second-level'
      } ${isNavigationMenuHidden ? 'hide' : ''} ${menuOpened ? 'opened' : ''}`}
    >
      {/* Mobile only */}
      <div className={'cmp-navigation-menu__selected-header'}>
        <div className={'cmp-navigation-menu__selected-header-text'}>
          <Text
            text={activeFirstLevel?.label}
            styleOption={'cmp-text__big bold cmp-navigation-menu__selected-first-level'}
            tagHtml={'span'}
          />
          {hasBooking && activeSecondLevel && (
            <Text
              text={activeSecondLevel?.label}
              styleOption={'cmp-text__normal bold cmp-navigation-menu__selected-second-level'}
              tagHtml={'span'}
            />
          )}
        </div>
        <Button
          icon={menuOpened ? 'up' : 'down'}
          accessibilityLabel={menuOpened ? menuCollapseAriaLabel : menuExpandAriaLabel}
          styleOption={'cmp-button__icon -icon-only -negative -no-outline'}
          onClick={() => setMenuOpened(!menuOpened)}
        />
      </div>

      <div
        className={`cmp-navigation-menu__panel ${menuOpened ? 'opened' : ''}`}
        aria-hidden={isPanelHidden ? true : undefined}
        {...(isPanelHidden ? { inert: '' } : {})}
      >
        {navigationItems?.map((item: NavigationRouteProps, index: number) => {
          return (
            <div
              key={`${item?.label} ${index}`}
              className={`cmp-navigation-menu__first-level ${index === 0 ? 'first-element' : ''} ${
                window.location.pathname.includes(item?.urlLink?.url ?? '') ? 'active' : ''
              } ${openIndex === index ? 'opened' : ''}`}
            >
              <Button
                buttonType={'button'}
                text={item?.label}
                buttonLink={item?.urlLink}
                icon={getFirstLevelItemIcon(item, index)}
                styleOption={`cmp-button__link -position-right cmp-navigation-menu__first-level-item ${
                  hasVisibleSecondLevelItems(item) ? '' : 'no-icon'
                } ${openIndex === index ? 'opened' : ''}`}
                onClick={(event) => {
                  handleClick(event, index);
                }}
              />

              {hasBooking && (deviceType !== 'desktop' || displaySecondItemsLevel(item)) && (
                <div className={'cmp-navigation-menu__second-level'}>
                  {item?.children?.map((child: any, index1: number) => {
                    const childRoute = child?.urlLink?.url?.replace(
                      item?.urlLink?.url?.replace('.html', ''),
                      ''
                    );

                    return (
                      <Button
                        key={`${child?.label} ${index1}`}
                        styleOption={`cmp-button__link cmp-navigation-menu__second-level-item ${
                          window.location.pathname.includes(childRoute) ? 'active' : ''
                        } ${
                          bookingStatus === BookingStatusEnum.OPTIONAL &&
                          loginMode === LoginMode.FULL &&
                          child?.hideForFullOPTBooking
                            ? 'hide'
                            : ''
                        }`}
                        text={child?.label}
                        accessibilityLabel={child?.label}
                        onClick={() => {
                          updateSelectedHeader(item, child, childRoute);
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
